import { memo, useCallback, useState } from 'react';
import { cn, Discount, InputQuantity, Price } from '@divlab/divanui';

import Img from '@UI/Img';
import capitalizeLine from '@Utils/capitalizeLine';
import styles from './OptionCard.module.css';

import type { FC, FormEvent, HTMLAttributes, MouseEvent } from 'react';
import type {
  ConfiguratorOptionItemFabric,
  ConfiguratorOptionItemParameter,
  ConfiguratorOptionItemProduct,
} from '@Pages/PageProduct/typings';

type ConfiguratorOptionItem =
  | ConfiguratorOptionItemFabric
  | ConfiguratorOptionItemProduct
  | ConfiguratorOptionItemParameter;

export interface OptionCardProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  item: ConfiguratorOptionItem;
  needCounter?: boolean;
  orientation?: 'landscape';
  onClickItem?: (e: MouseEvent, item: ConfiguratorOptionItem, overwrite?: boolean) => void;
}

const OptionCard: FC<OptionCardProps> = (props) => {
  const { className, item, orientation, needCounter, onClickItem, ...restProps } = props;
  const withCounter = needCounter && item.type === 'product';
  const [count, setCount] = useState(withCounter ? item.count : 0);

  const handleClickContainer = useCallback(
    (e: MouseEvent) => {
      if (withCounter) return;
      if (item.calcDisabled || item.selected || item.disabled) return;
      if (onClickItem) onClickItem(e, item);
    },
    [item, withCounter, onClickItem],
  );

  const handleChangeQuantity = useCallback(
    (e: FormEvent, { quantity }) => {
      if (item.calcDisabled) return;
      if (item.type === 'fabric') return;
      setCount(quantity);

      if (onClickItem) onClickItem(null, { ...item, count: quantity }, false);
    },
    [item, onClickItem],
  );

  return (
    <div
      {...restProps}
      className={cn(
        styles.card,
        { [styles.selected]: withCounter ? count > 0 : item.selected },
        { [styles.disabled]: item.calcDisabled },
        className,
      )}
      data-testid={item.testId}
    >
      <div className={styles.container} onClick={handleClickContainer}>
        <div
          className={cn(styles.wrapperImage, {
            [styles.orientationLandscape]: orientation === 'landscape' || withCounter,
          })}
        >
          <Img className={styles.imageWrapper} cnImage={styles.image} src={item.image} />
        </div>

        <div className={styles.content}>
          {withCounter && (
            <InputQuantity
              disabled={item.calcDisabled}
              className={styles.input}
              value={count}
              min={item.minCount}
              max={item.maxCount}
              onChange={handleChangeQuantity}
            />
          )}
          {capitalizeLine(item.title)}

          {!!item.price.actual && (
            <div className={styles.price}>
              <div className={styles.main}>
                {withCounter && `+`}
                <Price price={item.price.actual} className={styles.totalPrice} />
                {item.price.discount > 0 && (
                  <Discount className={styles.discount} value={item.price.discount} simple />
                )}
              </div>

              {item.price.expired > 0 && (
                <Price expired price={item.price.expired} className={styles.priceExpired} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(OptionCard);
